import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCredits } from '@/hooks/useCredits';
import { format } from 'date-fns';
import { Download, Filter, ArrowUpDown, ArrowUp, ArrowDown, History, Search, SortDesc } from 'lucide-react';
import { CreditTransaction } from '@/hooks/useCredits';

export function CreditTransactionHistory() {
  const { fetchTransactions } = useCredits();
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    type: 'all',
    category: 'all',
    search: '',
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const itemsPerPage = 50;

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const result = await fetchTransactions({
        limit: itemsPerPage,
        offset: page * itemsPerPage,
        type: filter.type !== 'all' ? filter.type : undefined,
        category: filter.category !== 'all' ? filter.category : undefined,
      });
      
      setTransactions(result.transactions);
      setHasMore(result.transactions.length === itemsPerPage);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, [page, filter.type, filter.category]);

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'consumption':
        return <ArrowDown className="w-4 h-4 text-red-500" />;
      case 'plan_allocation':
      case 'purchase':
        return <ArrowUp className="w-4 h-4 text-green-500" />;
      case 'refund':
        return <ArrowUp className="w-4 h-4 text-blue-500" />;
      default:
        return <ArrowUpDown className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTransactionBadge = (type: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      consumption: 'destructive',
      plan_allocation: 'default',
      purchase: 'secondary',
      refund: 'outline',
      adjustment: 'outline',
    };
    
    return (
      <Badge variant={variants[type] || 'default'} className="capitalize">
        {type.replace('_', ' ')}
      </Badge>
    );
  };

  const getCategoryBadge = (category: string | null) => {
    if (!category) return null;
    
    return (
      <Badge variant="outline" className="capitalize text-xs">
        {category.replace('_', ' ')}
      </Badge>
    );
  };

  const filteredTransactions = transactions.filter(transaction => {
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      return transaction.description.toLowerCase().includes(searchLower);
    }
    return true;
  });

  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  const exportTransactions = () => {
    const csv = [
      ['Date', 'Type', 'Category', 'Description', 'Amount', 'Balance After'],
      ...sortedTransactions.map(t => [
        format(new Date(t.created_at), 'yyyy-MM-dd HH:mm:ss'),
        t.type,
        t.category || '',
        t.description,
        t.amount.toString(),
        t.balance_after.toString(),
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `credit-transactions-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="bg-white rounded-xl border border-gray-200/50 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
      <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
              <History className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-1">
                Transaction History
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-200">
                View all your credit transactions and activity with detailed filtering
              </p>
            </div>
          </div>
          <Button 
            onClick={exportTransactions} 
            variant="outline" 
            size="sm"
            className="bg-white/80 backdrop-blur-sm border-blue-200 hover:bg-blue-50 hover:border-blue-300 transition-all duration-200"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>
      <CardContent className="p-6">
        {/* Enhanced Filters */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-blue-500" />
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">Search & Filter Transactions</h4>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search transactions..."
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                className="w-full bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border-gray-200 dark:border-gray-600 focus:border-blue-400 transition-colors"
              />
            </div>
            <Select
              value={filter.type}
              onValueChange={(value) => setFilter({ ...filter, type: value })}
            >
              <SelectTrigger className="w-[180px] bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border-gray-200 dark:border-gray-600">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="consumption">Consumption</SelectItem>
                <SelectItem value="plan_allocation">Plan Allocation</SelectItem>
                <SelectItem value="purchase">Purchase</SelectItem>
                <SelectItem value="refund">Refund</SelectItem>
                <SelectItem value="adjustment">Adjustment</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filter.category}
              onValueChange={(value) => setFilter({ ...filter, category: value })}
            >
              <SelectTrigger className="w-[180px] bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border-gray-200 dark:border-gray-600">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="content_generation">Content Generation</SelectItem>
                <SelectItem value="image_processing">Image Processing</SelectItem>
                <SelectItem value="video_processing">Video Processing</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border-gray-200 dark:border-gray-600 hover:from-blue-50 hover:to-indigo-50 hover:border-blue-300 transition-all duration-200"
            >
              <SortDesc className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Enhanced Transactions Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground font-medium">Loading transactions...</p>
          </div>
        ) : sortedTransactions.length === 0 ? (
          <div className="text-center py-12">
            <History className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No transactions found</h3>
            <p className="text-muted-foreground">Try adjusting your search filters</p>
          </div>
        ) : (
          <>
            <div className="rounded-xl border border-gray-200/50 bg-gradient-to-br from-white to-gray-50/30 dark:from-gray-900 dark:to-gray-800/30 overflow-hidden shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border-b border-gray-200/50">
                    <TableHead className="font-semibold text-gray-700 dark:text-gray-200">Date</TableHead>
                    <TableHead className="font-semibold text-gray-700 dark:text-gray-200">Type</TableHead>
                    <TableHead className="font-semibold text-gray-700 dark:text-gray-200">Description</TableHead>
                    <TableHead className="text-right font-semibold text-gray-700 dark:text-gray-200">Amount</TableHead>
                    <TableHead className="text-right font-semibold text-gray-700 dark:text-gray-200">Balance After</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTransactions.map((transaction) => (
                    <TableRow 
                      key={transaction.id} 
                      className="hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/50 dark:hover:from-blue-900/10 dark:hover:to-indigo-900/10 transition-colors duration-200 border-b border-gray-100/50 dark:border-gray-700/50"
                    >
                      <TableCell className="font-medium text-gray-900 dark:text-gray-100">
                        {format(new Date(transaction.created_at), 'MMM d, yyyy HH:mm')}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            {getTransactionIcon(transaction.type)}
                            {getTransactionBadge(transaction.type)}
                          </div>
                          {getCategoryBadge(transaction.category)}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-700 dark:text-gray-300 max-w-xs">
                        <div className="truncate" title={transaction.description}>
                          {transaction.description}
                        </div>
                      </TableCell>
                      <TableCell className={`text-right font-bold ${
                        transaction.amount < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                      }`}>
                        {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-gray-900 dark:text-gray-100">
                        {transaction.balance_after.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Enhanced Pagination */}
            <div className="flex items-center justify-between mt-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-lg">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Showing <span className="text-blue-600 dark:text-blue-400 font-bold">{sortedTransactions.length}</span> transactions
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 0}
                  className="bg-white/80 dark:bg-gray-900/80 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  <ArrowUp className="w-4 h-4 mr-1 rotate-[-90deg]" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={!hasMore}
                  className="bg-white/80 dark:bg-gray-900/80 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  Next
                  <ArrowUp className="w-4 h-4 ml-1 rotate-[90deg]" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
      <div className="h-2 bg-gradient-to-r from-blue-200 via-indigo-200 to-purple-200 dark:from-blue-800 dark:via-indigo-800 dark:to-purple-800" />
    </Card>
  );
}